import deleteCompanyQuoteModuleApi from '@/api/delete/company/delete-company-quote-module';
import getQuoteModulesApi from '@/api/get/company/quote-modules/get-quote-modules';
import updateCompanyQuoteModuleApi from '@/api/patch/company/company-quote-module/update-company-quote-module';
import createCompanyQuoteModuleApi from '@/api/post/company/company-quote-module/create-company-quote-module';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import Loader from '@/components/loader/loader-component';
import Modal from '@/components/modal/modal-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import { removeUndefinedProperties } from '@/utils/utils';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import QuoteModuleDetails from './quote-module-details';
import QuoteModuleFormWrapper from './quote-module-form-wrapper';
import QuoteModulePrice from './quote-module-price';
import Styled from './quote-module-style';

/**
 * Quote Module component
 * @returns {JSX.Element} component
 */
const QuoteModule = () => {
  const { id: companyId } = useParams();
  const [activeTab, setActiveTab] = useState('Price');
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [loadingState, setLoadingState] = useState(true);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [initLoad, setInitLoad] = useState(true);
  const [moduleIdToEdit, setModuleIdToEdit] = useState(null);
  const [moduleIdToDelete, setModuleIdToDelete] = useState(null);
  const [data, setData] = useState([]);

  const getApi = useCallback(() => {
    const getModuleApi = async () => {
      const awaitRes = await getQuoteModulesApi(companyId);
      setData(awaitRes.data);
      setLoadingState(false);
    };
    getModuleApi();
  }, [companyId]);

  useEffect(() => {
    if (initLoad) {
      getApi();
      setInitLoad(false);
    }
  }, [getApi, initLoad]);

  const [createQuoteModule, { loading: createQuoteModuleLoading }] =
    useMutation(createCompanyQuoteModuleApi);

  const handleCreateQuoteModel = (submitValues) => {
    createQuoteModule(removeUndefinedProperties(submitValues), companyId)
      .then(() => {
        setOpenCreateModal(false);
        getApi();
        Alert({
          icon: 'success',
          text: 'Quote Module created successfully',
        });
      })
      .catch(() => {
        setOpenCreateModal(false);
        Alert({
          icon: 'error',
          text: 'There was a problem creating the quote Module',
        });
      });
  };

  const [updateQuoteModule, { loading: updateQuoteModuleLoading }] =
    useMutation(updateCompanyQuoteModuleApi);

  const handleUpdateQuoteModel = (submitValues) => {
    updateQuoteModule(
      companyId,
      moduleIdToEdit,
      removeUndefinedProperties(submitValues),
    )
      .then(() => {
        setOpenEditModal(false);
        setModuleIdToEdit(null);
        getApi();
        Alert({
          icon: 'success',
          text: 'Quote Module updated successfully',
        });
      })
      .catch(() => {
        setOpenEditModal(false);
        setModuleIdToEdit(null);
        Alert({
          icon: 'error',
          text: 'There was a problem updating the quote Module',
        });
      });
  };

  const [deleteCompanyQuoteModule, { loading: deleteCompanyOptionLoading }] =
    useMutation(deleteCompanyQuoteModuleApi);

  const launchDeleteConfirmation = useCallback(() => {
    if (moduleIdToDelete) {
      ConfirmationModal({
        title: 'Delete Module',
        text: `Are you sure you want to delete this module?`,
        onConfirm: () =>
          deleteCompanyQuoteModule(companyId, moduleIdToDelete).then(() => {
            getApi();
            setModuleIdToDelete(null);
            Alert({
              icon: 'success',
              text: 'Module deleted successfully',
            });
          }),
      });
    }
  }, [companyId, deleteCompanyQuoteModule, getApi, moduleIdToDelete]);

  const priceItems = data.filter((item) => item.type === 'price');
  const detailsItems = data.filter((item) => item.type === 'details');

  const priceData = [...priceItems];
  const detailsData = [...detailsItems];

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  const isLoading =
    createQuoteModuleLoading ||
    loadingState ||
    deleteCompanyOptionLoading ||
    updateQuoteModuleLoading;

  if (isLoading) {
    return (
      <PageWrapper>
        <Styled.CardStyle>
          <Loader loading />
        </Styled.CardStyle>
      </PageWrapper>
    );
  }
  return (
    <PageWrapper>
      <Styled.CardStyle>
        <Styled.RowStyle>
          <Styled.TabContainer>
            <Styled.Tab
              active={activeTab === 'Price'}
              onClick={() => handleTabClick('Price')}
            >
              <Styled.Label active={activeTab === 'Price'}>Price</Styled.Label>
            </Styled.Tab>
            <Styled.Tab
              active={activeTab === 'Details'}
              onClick={() => handleTabClick('Details')}
            >
              <Styled.Label active={activeTab === 'Details'}>
                Details
              </Styled.Label>
            </Styled.Tab>
          </Styled.TabContainer>
          <Styled.ButtonDiv>
            <PrimaryButton
              title="New Module"
              buttonWidth="fit-content"
              type="button"
              onClick={() => {
                setOpenCreateModal(true);
              }}
            />
          </Styled.ButtonDiv>
        </Styled.RowStyle>
        {activeTab === 'Price' && (
          <QuoteModulePrice
            setModuleIdToDelete={setModuleIdToDelete}
            deleteQuoteModuleHandler={launchDeleteConfirmation}
            setOpenCreateModal={setOpenCreateModal}
            setOpenEditModal={setOpenEditModal}
            setModuleIdToEdit={setModuleIdToEdit}
            data={priceData}
          />
        )}
        {activeTab === 'Details' && (
          <QuoteModuleDetails
            setModuleIdToDelete={setModuleIdToDelete}
            deleteQuoteModuleHandler={launchDeleteConfirmation}
            setOpenCreateModal={setOpenCreateModal}
            setOpenEditModal={setOpenEditModal}
            setModuleIdToEdit={setModuleIdToEdit}
            data={detailsData}
          />
        )}
        {openCreateModal && (
          <Modal
            width="500px"
            open={openCreateModal}
            height="auto"
            showClose
            bodyStyle={{
              padding: '0px',
            }}
            close={() => {
              setOpenCreateModal(false);
            }}
            title="New Module"
          >
            <QuoteModuleFormWrapper
              moduleToEditId={moduleIdToEdit}
              submit={handleCreateQuoteModel}
              onClose={() => {
                setOpenCreateModal(false);
                setModuleIdToEdit(null);
              }}
              type="new"
            />
          </Modal>
        )}
        {moduleIdToEdit && (
          <Modal
            width="500px"
            open={openEditModal}
            height="auto"
            showClose
            bodyStyle={{
              padding: '0px',
            }}
            close={() => {
              setOpenEditModal(false);
            }}
            title="Edit Module"
          >
            <QuoteModuleFormWrapper
              submit={handleUpdateQuoteModel}
              onClose={() => {
                setOpenEditModal(false);
                setModuleIdToEdit(null);
              }}
              companyId={companyId}
              moduleIdToEdit={moduleIdToEdit}
              type="edit"
            />
          </Modal>
        )}
      </Styled.CardStyle>
    </PageWrapper>
  );
};

export default QuoteModule;
